import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../../lib/api.js';
import type { AuditEvent, Concept } from '../../lib/types.js';
import { StatusBadge } from '../../components/StatusBadge.js';
import { ConceptPrompts } from '../../components/ConceptPrompts.js';

export function ConceptDetailPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const conceptQuery = useQuery({
    queryKey: ['concepts', id],
    queryFn: () => api.get<Concept>(`/concepts/${id}`),
  });
  const auditQuery = useQuery({
    queryKey: ['concepts', id, 'audit'],
    queryFn: () => api.get<AuditEvent[]>(`/concepts/${id}/audit`),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['concepts', id] });
    await queryClient.invalidateQueries({ queryKey: ['concepts', id, 'audit'] });
    await queryClient.invalidateQueries({ queryKey: ['concepts'] });
  };

  const submit = useMutation({
    mutationFn: () => api.post<Concept>(`/concepts/${id}/submit`),
    onSuccess: invalidate,
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Submit failed'),
  });
  const review = useMutation({
    mutationFn: (decision: 'approved' | 'rejected' | 'archived') =>
      api.post<Concept>(`/concepts/${id}/review`, {
        decision,
        resolutionNote: note || undefined,
      }),
    onSuccess: invalidate,
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Review failed'),
  });

  if (conceptQuery.isLoading) return <p>Loading…</p>;
  if (conceptQuery.isError || !conceptQuery.data) {
    return (
      <p role="alert" className="form-error">
        Concept not found.
      </p>
    );
  }
  const c = conceptQuery.data.data;

  return (
    <section>
      <p>
        <Link to="/concepts">← Back to concepts</Link>
      </p>
      <div className="page-head">
        <h1>{c.title}</h1>
        <span>
          <StatusBadge status={c.status} /> <StatusBadge status={c.ipRisk} />
        </span>
      </div>

      <dl className="detail-grid">
        <dt>Consumer need</dt>
        <dd>{c.consumerNeed}</dd>
        <dt>Target audience</dt>
        <dd>{c.targetAudience}</dd>
        <dt>Differentiation</dt>
        <dd>{c.differentiation}</dd>
        <dt>Formats</dt>
        <dd>{c.productFormats.join(', ')}</dd>
        <dt>Opportunity</dt>
        <dd>{Math.round(c.opportunityScore.total * 100)}%</dd>
        <dt>Assumptions</dt>
        <dd>{c.assumptions.length ? c.assumptions.join('; ') : '—'}</dd>
      </dl>

      {c.ipRisk === 'red' && (
        <div className="policy-note" role="note" style={{ borderLeftColor: 'var(--bad)' }}>
          <strong>Red IP risk:</strong> this concept cannot be approved.
        </div>
      )}

      <div className="review-panel">
        <h2>Actions</h2>
        {c.status === 'draft' && (
          <button type="button" onClick={() => submit.mutate()} disabled={submit.isPending}>
            Submit for review
          </button>
        )}
        {c.status === 'needs_review' && (
          <>
            {c.ipRisk === 'yellow' && (
              <>
                <label htmlFor="note">Resolution note (required for yellow risk)</label>
                <textarea
                  id="note"
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </>
            )}
            <div className="button-row">
              <button
                type="button"
                onClick={() => review.mutate('approved')}
                disabled={review.isPending || c.ipRisk === 'red'}
              >
                Approve
              </button>
              <button
                type="button"
                className="button--danger"
                onClick={() => review.mutate('rejected')}
                disabled={review.isPending}
              >
                Reject
              </button>
              <button
                type="button"
                onClick={() => review.mutate('archived')}
                disabled={review.isPending}
              >
                Archive
              </button>
            </div>
          </>
        )}
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
      </div>

      {c.status === 'approved' && <ConceptPrompts conceptId={c.id} />}

      <h2>Audit timeline</h2>
      {auditQuery.data && auditQuery.data.data.length > 0 ? (
        <ul className="audit-list">
          {auditQuery.data.data.map((e) => (
            <li key={e._id}>
              <code>{e.action}</code> · {new Date(e.occurredAt).toLocaleString()}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">No audit events.</p>
      )}
    </section>
  );
}
